/*
  # Scientific Publication Modal Component
  
  1. Changes
    - Created modal for adding/editing scientific publications
    - Added file upload for PDFs and images
    - Form validation and submission to database
*/

import React, { useState, useEffect } from 'react';
import { Upload, X, Plus, Minus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { validateImage, convertToWebP } from '../../utils/imageUtils';
import { ScientificPublication, PublicationCategory } from '../../types/scientific-publications';

interface ScientificPublicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  publication: ScientificPublication | null;
  onSave: () => void;
}

const categories: { value: PublicationCategory; label: string }[] = [
  { value: 'amphibians', label: 'Amphibians' },
  { value: 'reptiles', label: 'Reptiles' },
  { value: 'ecology', label: 'Ecology' },
  { value: 'behavior', label: 'Behavior' },
  { value: 'conservation', label: 'Conservation' }
];

const ScientificPublicationModal: React.FC<ScientificPublicationModalProps> = ({
  isOpen,
  onClose,
  publication,
  onSave
}) => {
  const [formData, setFormData] = useState({
    title: '',
    authors: [''],
    journal: '',
    year: new Date().getFullYear(),
    category: 'amphibians' as PublicationCategory,
    abstract: '',
    pdf_url: '',
    image_url: '',
    doi: ''
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (publication) {
      setFormData({
        title: publication.title,
        authors: publication.authors,
        journal: publication.journal,
        year: publication.year,
        category: publication.category,
        abstract: publication.abstract,
        pdf_url: publication.pdf_url,
        image_url: publication.image_url,
        doi: publication.doi || ''
      });
    } else {
      setFormData({
        title: '',
        authors: [''],
        journal: '',
        year: new Date().getFullYear(),
        category: 'amphibians',
        abstract: '',
        pdf_url: '',
        image_url: '',
        doi: ''
      });
    }
  }, [publication]);

  const uploadFile = async (file: File, type: 'pdf' | 'image') => {
    try {
      setUploading(true);
      setError(null);

      let processedFile = file;
      let fileName = `${Math.random()}.${file.name.split('.').pop()}`;
      let filePath = `scientific-work/${fileName}`;

      if (type === 'image') {
        const validationError = validateImage(file);
        if (validationError) {
          setError(validationError);
          return null;
        }
        processedFile = await convertToWebP(file);
        fileName = `${Math.random()}.webp`;
        filePath = `scientific-work/${fileName}`;
      }

      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, processedFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('gallery')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'pdf' | 'image') => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (type === 'pdf' && file.type !== 'application/pdf') {
      setError('Please select a PDF file');
      return;
    }

    const url = await uploadFile(file, type);
    if (url) {
      setFormData(prev => ({
        ...prev,
        [type === 'pdf' ? 'pdf_url' : 'image_url']: url
      }));
    }
  };

  const addAuthor = () => {
    setFormData(prev => ({
      ...prev,
      authors: [...prev.authors, '']
    }));
  };

  const removeAuthor = (index: number) => {
    setFormData(prev => ({
      ...prev,
      authors: prev.authors.filter((_, i) => i !== index)
    }));
  };

  const updateAuthor = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      authors: prev.authors.map((author, i) => i === index ? value : author)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    const filteredAuthors = formData.authors.filter(author => author.trim() !== '');
    if (filteredAuthors.length === 0) {
      setError('At least one author is required');
      return;
    }

    if (!formData.journal.trim()) {
      setError('Journal is required');
      return;
    }

    if (!formData.year || formData.year < 1900 || formData.year > 2100) {
      setError('Please enter a valid year');
      return;
    }

    if (!formData.pdf_url) {
      setError('Please upload a PDF file');
      return;
    }

    try {
      const data = {
        title: formData.title,
        authors: filteredAuthors,
        journal: formData.journal,
        year: formData.year,
        category: formData.category,
        pdf_url: formData.pdf_url,
        doi: formData.doi || null
      };

      if (publication) {
        const { error } = await supabase
          .from('scientific_publications')
          .update(data)
          .eq('id', publication.id);
        if (error) throw error;
      } else {
        // Get the highest display order
        const { data: existingPubs, error: orderError } = await supabase
          .from('scientific_publications')
          .select('display_order')
          .order('display_order', { ascending: false })
          .limit(1);

        if (orderError) throw orderError;

        const nextOrder = existingPubs && existingPubs.length > 0
          ? existingPubs[0].display_order + 1
          : 0;

        const { error } = await supabase
          .from('scientific_publications')
          .insert([{ ...data, display_order: nextOrder }]);
        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving publication:', err);
      setError('Failed to save publication');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold">
            {publication ? 'Edit Publication' : 'Add New Publication'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-white/70 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              required
            />
          </div>

          {/* Authors */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Authors *
            </label>
            {formData.authors.map((author, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={author}
                  onChange={(e) => updateAuthor(index, e.target.value)}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                  placeholder="Author name"
                />
                {formData.authors.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAuthor(index)}
                    className="p-2 text-red-500 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addAuthor}
              className="flex items-center gap-2 px-3 py-1 text-sm bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Author
            </button>
          </div>

          {/* Journal and Year */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="journal" className="block text-sm font-medium text-white/70 mb-2">
                Journal *
              </label>
              <input
                type="text"
                id="journal"
                value={formData.journal}
                onChange={(e) => setFormData({ ...formData, journal: e.target.value })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                required
              />
            </div>
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-white/70 mb-2">
                Year *
              </label>
              <input
                type="number"
                id="year"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
                min="1900"
                max="2100"
                required
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-white/70 mb-2">
              Category *
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as PublicationCategory })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              required
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value} className="bg-[#1F2B1F]">
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Abstract */}

          {/* DOI */}
          <div>
            <label htmlFor="doi" className="block text-sm font-medium text-white/70 mb-2">
              DOI (optional)
            </label>
            <input
              type="text"
              id="doi"
              value={formData.doi}
              onChange={(e) => setFormData({ ...formData, doi: e.target.value })}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white"
              placeholder="10.1234/example.doi"
            />
          </div>

          {/* File Uploads */}
          <div>
            {/* PDF Upload */}
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                PDF File *
              </label>
              {formData.pdf_url ? (
                <div className="relative">
                  <div className="p-4 bg-white/10 rounded-lg border border-white/20">
                    <p className="text-sm text-white">PDF uploaded successfully</p>
                    <a
                      href={formData.pdf_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#E85C0D] hover:underline text-sm"
                    >
                      View PDF
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, pdf_url: '' })}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded-full"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <label className="relative cursor-pointer block">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileUpload(e, 'pdf')}
                    disabled={uploading}
                    className="hidden"
                  />
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-6 text-center hover:border-white/40 transition-colors">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-white/40" />
                    <span className="text-sm text-white/40">
                      {uploading ? 'Uploading...' : 'Click to upload PDF'}
                    </span>
                  </div>
                </label>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-6 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : publication ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScientificPublicationModal;